import { useCallback, useEffect, useRef, useState } from 'react';
import {
  classifyPageTypeFromPathname,
  GREETING_POPUP_MAX_WAIT_MS,
  resolveContextPopupDelaySeconds,
  resolveProactiveGreetingDelaySeconds,
} from '@teo/widget-shared';
import {
  attachExitIntent,
  clearContextPopupDismissed,
  clearGreetingOfferState,
  ensurePageContextParserLoaded,
  getProactiveGreetingCache,
  getWidgetSession,
  isContextPopupDismissed,
  isExitIntentDismissed,
  logPopup,
  markContextPopupDismissed,
  markExitIntentDismissed,
  markGreetingOfferDismissed,
  markGreetingOfferShownFromEntry,
  markProactiveChatInteraction,
  markProactivePopupShown,
  markHumanReadyFromUi,
  prefetchProactiveGreeting,
  reportWidgetEvent,
  shouldAllowProactivePopup,
  startPageBehaviorWatchers,
  startPopupRetentionTracking,
  startProactiveDwellTracking,
  stopPopupRetentionTracking,
  isHumanGateEnabled,
  isHumanReady,
  whenHumanReady,
  touchProactiveSession,
  useWidgetModulesOptional,
  waitForProactiveGreeting,
  watchPageNavigation,
} from '@teo/widget-core';
import { useWidget } from '../context/WidgetContext.js';

export type UseContextPopupsOptions = {
  enabled?: boolean;
  chatOpen: boolean;
  apiBase?: string;
  mock?: boolean;
  /** Open full chat after proactive greeting delay (instead of popup bubble). */
  onAutoOpenChat?: () => void;
};

export type UseContextPopupsResult = {
  popupVisible: boolean;
  popupMessage: string;
  popupCtaText?: string;
  dismissPopup: () => void;
  /** Hide popup after CTA — keeps greeting offer pending for chat/send. */
  acceptPopup: () => void;
  popupKind: 'exit-intent' | 'context-ai' | null;
};
export function useContextPopups({
  enabled = true,
  chatOpen,
  apiBase,
  mock = false,
  onAutoOpenChat,
}: UseContextPopupsOptions): UseContextPopupsResult {
  const { settings, locale, exitIntentText } = useWidget();
  const modules = useWidgetModulesOptional();
  const [popupVisible, setPopupVisible] = useState(false);
  const [popupMessage, setPopupMessage] = useState('');
  const [popupCtaText, setPopupCtaText] = useState<string | undefined>();
  const [popupKind, setPopupKind] = useState<'exit-intent' | 'context-ai' | null>(
    null,
  );

  const popupKindRef = useRef(popupKind);
  const pathnameRef = useRef(
    typeof window !== 'undefined' ? window.location.pathname : '/',
  );
  const contextTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const contextRequestRef = useRef(0);
  const pageEnteredAtRef = useRef(0);
  const chatOpenRef = useRef(chatOpen);
  const wasChatOpenRef = useRef(chatOpen);
  chatOpenRef.current = chatOpen;
  popupKindRef.current = popupKind;
  const onAutoOpenChatRef = useRef(onAutoOpenChat);
  onAutoOpenChatRef.current = onAutoOpenChat;

  const resolveSlug = useCallback((): string | null => {
    const session = getWidgetSession();
    if (session?.slug) return session.slug;
    if (typeof document !== 'undefined') {
      const fromScript = document
        .querySelector('script[data-slug]')
        ?.getAttribute('data-slug')
        ?.trim();
      if (fromScript) return fromScript;
    }
    if (mock) return 'teo-playground-mock';
    return null;
  }, [mock]);

  const evaluateProactiveAllowance = useCallback(
    (slug: string) =>
      shouldAllowProactivePopup({
        mode: settings.content.proactiveGreetingMode,
        reengageSeconds: settings.content.proactiveGreetingReengageSeconds,
        slug,
      }),
    [
      settings.content.proactiveGreetingMode,
      settings.content.proactiveGreetingReengageSeconds,
    ],
  );

  const hidePopup = useCallback(() => {
    if (popupKindRef.current) {
      logPopup('ui', 'hide popup', { kind: popupKindRef.current });
    }
    stopPopupRetentionTracking();
    setPopupVisible(false);
    setPopupMessage('');
    setPopupCtaText(undefined);
    setPopupKind(null);
  }, []);

  const dismissPopup = useCallback(() => {
    const slug = resolveSlug();
    const kind = popupKindRef.current;

    logPopup('ui', 'dismiss popup', { kind, slug, pathname: pathnameRef.current });

    if (kind) {
      reportWidgetEvent('popup_dismissed', {
        kind,
        pathname: pathnameRef.current,
      });
    }

    if (slug && kind === 'exit-intent') {
      markExitIntentDismissed(slug);
    }
    if (slug && kind === 'context-ai') {
      markContextPopupDismissed(slug, pathnameRef.current);
      markGreetingOfferDismissed(slug, pathnameRef.current);
    }

    hidePopup();
  }, [hidePopup, resolveSlug]);

  /**
   * CTA / open chat from popup: suppress popup frequency, keep greeting offer
   * as `shown` so it can be committed on the next user message.
   */
  const acceptPopup = useCallback(() => {
    const slug = resolveSlug();
    const kind = popupKindRef.current;

    logPopup('ui', 'accept popup → chat', {
      kind,
      slug,
      pathname: pathnameRef.current,
    });

    if (slug && kind === 'exit-intent') {
      markExitIntentDismissed(slug);
    }
    if (slug && kind === 'context-ai') {
      markContextPopupDismissed(slug, pathnameRef.current);
      const entry = getProactiveGreetingCache(slug, pathnameRef.current);
      if (entry) {
        markGreetingOfferShownFromEntry(entry);
      }
    }

    hidePopup();
  }, [hidePopup, resolveSlug]);

  const showPopup = useCallback(
    (
      kind: 'exit-intent' | 'context-ai',
      message: string,
      ctaText?: string | null,
      meta?: {
        trigger?: 'move' | 'leave';
        delaySeconds?: number;
        mock?: boolean;
        pageType?: string;
      },
    ): boolean => {
      const trimmed = message.trim();
      if (!trimmed) {
        logPopup('ui', 'show skipped — empty message', { kind }, 'warn');
        return false;
      }
      if (chatOpenRef.current) {
        logPopup('ui', 'show skipped — chat open', { kind });
        return false;
      }

      logPopup('ui', 'show popup', {
        kind,
        messagePreview: trimmed.slice(0, 80),
        pathname: pathnameRef.current,
      });

      setPopupKind(kind);
      setPopupMessage(trimmed);
      setPopupCtaText(ctaText?.trim() || undefined);
      setPopupVisible(true);

      const slug = resolveSlug();
      if (slug) {
        // Timed context greeting may become a chat offer; exit-intent is
        // settings-only text and must not claim the /greeting offer.
        if (kind === 'context-ai') {
          const entry = getProactiveGreetingCache(slug, pathnameRef.current);
          if (entry) {
            markGreetingOfferShownFromEntry(entry);
          }
        }
        markProactivePopupShown(slug);
      }

      reportWidgetEvent(
        'popup_shown',
        {
          kind,
          pathname: pathnameRef.current,
          message_length: trimmed.length,
          has_cta: Boolean(ctaText?.trim()),
          trigger: meta?.trigger,
          delay_seconds: meta?.delaySeconds,
          mock: meta?.mock,
        },
        { locale },
      );
      startPopupRetentionTracking(kind);

      return true;
    },
    [locale, resolveSlug],
  );

  const cancelContextSchedule = useCallback(() => {
    if (contextTimerRef.current) {
      logPopup('context', 'cancel scheduled timer');
      clearTimeout(contextTimerRef.current);
      contextTimerRef.current = null;
    }
    contextRequestRef.current += 1;
  }, []);

  const scheduleContextPopup = useCallback((): boolean => {
    cancelContextSchedule();

    const pathname =
      typeof window !== 'undefined' ? window.location.pathname : '/';
    pathnameRef.current = pathname;
    const slug = resolveSlug();
    const pageType = classifyPageTypeFromPathname(pathname);

    const reportContextSkip = (reason: string) => {
      reportWidgetEvent('context_popup_skipped', { pathname, reason, page_type: pageType });
    };

    if (!enabled) {
      logPopup('context', 'skip — hook disabled');
      reportContextSkip('hook_disabled');
      return false;
    }
    // Use ref so closing chat does not recreate this callback / remount the
    // navigation watcher and reschedule a duplicate greeting popup.
    if (chatOpenRef.current) {
      logPopup('context', 'skip — chat open');
      reportContextSkip('chat_open');
      return false;
    }
    if (!modules?.isEnabled('contextPopup') && modules != null) {
      logPopup('context', 'skip — contextPopup module off');
      reportContextSkip('module_off');
      return false;
    }
    if (modules == null && !settings.content.proactiveGreetingEnabled) {
      logPopup('context', 'skip — PROACTIVE_GREETING_ENABLED false');
      reportContextSkip('proactive_greeting_disabled');
      return false;
    }
    if (!apiBase) {
      logPopup('context', 'skip — no apiBase');
      reportContextSkip('no_api_base');
      return false;
    }
    if (!slug) {
      logPopup('context', 'skip — no slug/session');
      reportContextSkip('no_slug');
      return false;
    }

    touchProactiveSession(slug);
    const allowance = evaluateProactiveAllowance(slug);
    if (!allowance.allow) {
      logPopup('context', 'skip — proactive mode gate', {
        mode: settings.content.proactiveGreetingMode,
        reason: allowance.reason,
      });
      reportContextSkip(allowance.reason);
      return false;
    }

    const humanGateOn =
      (modules?.isEnabled('humanGate') ?? true) && isHumanGateEnabled();
    if (humanGateOn && !isHumanReady()) {
      logPopup('context', 'defer — awaiting human-gate');
      void whenHumanReady().then(() => {
        if (chatOpenRef.current) return;
        scheduleContextPopup();
      });
      return false;
    }

    // Kick off /greeting only when popup may show (incl. dwell re-engage).
    void prefetchProactiveGreeting({
      apiBase,
      slug,
      locale,
      mock,
      url: typeof window !== 'undefined' ? window.location.href : undefined,
      pageContextEnabled: settings.content.contextEnabled,
      flushEventsFirst: true,
      quickReplyCount:
        settings.content.proactiveGreetingQuickReplies &&
        settings.content.proactiveGreetingQuickReplySource !== 'custom'
          ? settings.content.proactiveGreetingQuickReplyCount
          : undefined,
    });

    const configuredDelay = resolveContextPopupDelaySeconds(
      pageType,
      settings.content.contextPopupDelayByPageType,
      settings.content.contextPopupDelaySeconds,
    );

    if (isContextPopupDismissed(slug, pathname)) {
      logPopup('context', 'skip — suppressed for this page visit', {
        slug,
        pathname,
      });
      reportContextSkip('dismissed');
      return false;
    }

    const requestId = ++contextRequestRef.current;
    pageEnteredAtRef.current = Date.now();
    const autoOpenChat = settings.content.proactiveGreetingAutoOpenChat;

    const showGreetingPopup = async () => {
      if (requestId !== contextRequestRef.current) {
        logPopup('context', 'timer stale — skipped', { requestId }, 'debug');
        return;
      }
      if (chatOpenRef.current) {
        logPopup('context', 'timer fired — chat open, skip');
        return;
      }

      logPopup('context', 'show prefetched greeting', { requestId });

      const startedAt = Date.now();
      reportWidgetEvent('context_popup_fetch_started', {
        pathname,
        mock,
      });

      try {
        const entry = getProactiveGreetingCache(slug, pathname);

        if (requestId !== contextRequestRef.current) return;
        if (isContextPopupDismissed(slug, pathname)) {
          logPopup('context', 'skip after greeting — dismissed', { pathname });
          return;
        }

        if (!entry || entry.status !== 'ready') {
          reportWidgetEvent('context_popup_fetch_failed', {
            pathname,
            latency_ms: Date.now() - startedAt,
            error: entry?.error ?? 'greeting_unavailable',
            mock,
          });
          logPopup('context', 'greeting unavailable', { entry }, 'warn');
          return;
        }

        if (!entry.shouldShow || !entry.displayText) {
          reportWidgetEvent('context_popup_skipped', {
            pathname,
            reason: 'greeting_should_not_show',
          });
          return;
        }

        const latencyMs = Date.now() - startedAt;
        reportWidgetEvent('context_popup_fetch_succeeded', {
          pathname,
          latency_ms: latencyMs,
          message_length: entry.displayText.length,
          mock,
        });

        const delaySeconds =
          entry.delaySeconds ??
          (entry.response
            ? resolveProactiveGreetingDelaySeconds(
                entry.response,
                pageType,
                settings.content.contextPopupDelayByPageType,
                settings.content.contextPopupDelaySeconds,
              )
            : configuredDelay);

        if (autoOpenChat) {
          markContextPopupDismissed(slug, pathname);
          markGreetingOfferShownFromEntry(entry);
          markProactivePopupShown(slug);
          logPopup('context', 'auto-open chat', {
            pathname,
            pageType,
            delaySeconds,
          });
          onAutoOpenChatRef.current?.();
          return;
        }

        showPopup('context-ai', entry.displayText, undefined, {
          delaySeconds,
          mock,
          pageType,
        });
      } catch (error) {
        const latencyMs = Date.now() - startedAt;
        reportWidgetEvent('context_popup_fetch_failed', {
          pathname,
          latency_ms: latencyMs,
          error: error instanceof Error ? error.message : String(error),
          mock,
        });
        logPopup('context', 'greeting resolve failed', { error, mock }, 'warn');
      }
    };

    const armTimer = async () => {
      const existing = getProactiveGreetingCache(slug, pathname);
      const waitResult =
        existing?.status === 'ready' || existing?.status === 'error'
          ? { entry: existing, timedOut: false }
          : await waitForProactiveGreeting(slug, pathname, {
              timeoutMs: GREETING_POPUP_MAX_WAIT_MS,
            });

      if (requestId !== contextRequestRef.current) return;

      const entry = waitResult.entry;
      if (
        !entry ||
        entry.status === 'error' ||
        entry.status === 'aborted' ||
        entry.status === 'loading' ||
        waitResult.timedOut
      ) {
        reportWidgetEvent('context_popup_fetch_failed', {
          pathname,
          latency_ms: 0,
          error:
            entry?.status === 'loading' || waitResult.timedOut
              ? 'fetch_timeout'
              : (entry?.error ?? 'greeting_unavailable'),
          mock,
        });
        logPopup('context', 'greeting prefetch failed — skip schedule', {
          entry,
          timedOut: waitResult.timedOut,
        }, 'warn');
        return;
      }

      if (!entry.shouldShow || entry.greetingPhrases.length === 0) {
        reportContextSkip('greeting_should_not_show');
        return;
      }

      const delaySeconds =
        entry.delaySeconds ??
        resolveProactiveGreetingDelaySeconds(
          entry.response!,
          pageType,
          settings.content.contextPopupDelayByPageType,
          settings.content.contextPopupDelaySeconds,
        );

      if (!Number.isFinite(delaySeconds) || delaySeconds < 0) {
        logPopup('context', 'skip — delay disabled', { delaySeconds, pageType });
        reportContextSkip('delay_disabled');
        return;
      }
      if (delaySeconds === 0) {
        logPopup('context', 'skip — delay zero', { pageType });
        reportContextSkip('delay_disabled');
        return;
      }

      reportWidgetEvent('context_popup_scheduled', {
        pathname,
        page_type: pageType,
        delay_seconds: delaySeconds,
        auto_open_chat: autoOpenChat,
        mock,
      });

      logPopup('context', 'scheduled', {
        slug,
        pathname,
        pageType,
        delaySeconds,
        autoOpenChat,
        mock,
        requestId,
      });

      // Wait is already done — count delay from now (not stale pageEnteredAt).
      const remaining = delaySeconds * 1000;

      contextTimerRef.current = setTimeout(() => {
        contextTimerRef.current = null;
        void showGreetingPopup();
      }, remaining);
    };

    void armTimer();
    return true;
  }, [
    apiBase,
    cancelContextSchedule,
    enabled,
    evaluateProactiveAllowance,
    locale,
    mock,
    modules,
    resolveSlug,
    settings.content.contextEnabled,
    settings.content.proactiveGreetingEnabled,
    settings.content.proactiveGreetingAutoOpenChat,
    settings.content.proactiveGreetingMode,
    settings.content.proactiveGreetingReengageSeconds,
    settings.content.contextPopupDelaySeconds,
    settings.content.contextPopupDelayByPageType,
    showPopup,
  ]);

  useEffect(() => {
    if (!enabled || !settings.content.contextEnabled) return;

    void ensurePageContextParserLoaded();
    return startPageBehaviorWatchers();
  }, [enabled, settings.content.contextEnabled]);

  // Chat open: hide popup + cancel timer. Suppress this visit only if a popup
  // was on screen. Chat close: always re-arm context timer (clear visit suppress
  // so the schedule can actually fire after engagement).
  useEffect(() => {
    if (!enabled) {
      wasChatOpenRef.current = chatOpen;
      return;
    }

    const wasOpen = wasChatOpenRef.current;
    wasChatOpenRef.current = chatOpen;

    if (chatOpen) {
      const slug = resolveSlug();
      // Close proactive cycle — wait for dwell / return before every-page again.
      if (slug) {
        markProactiveChatInteraction(slug);
      }
      const kind = popupKindRef.current;
      if (slug && kind === 'context-ai') {
        markContextPopupDismissed(slug, pathnameRef.current);
        logPopup('context', 'chat open with popup — suppress visit', {
          slug,
          pathname: pathnameRef.current,
        });
      } else if (slug && kind === 'exit-intent') {
        markExitIntentDismissed(slug);
      } else {
        logPopup('context', 'chat open — cancel schedule (no popup shown)', {
          pathname: pathnameRef.current,
        });
      }
      hidePopup();
      cancelContextSchedule();
      return;
    }

    if (!wasOpen) return;

    const slug = resolveSlug();
    if (slug) {
      clearContextPopupDismissed(slug, pathnameRef.current);
    }
    logPopup('context', 'chat closed — re-arm timer', {
      slug,
      pathname: pathnameRef.current,
    });
    scheduleContextPopup();
  }, [
    cancelContextSchedule,
    chatOpen,
    enabled,
    hidePopup,
    resolveSlug,
    scheduleContextPopup,
  ]);

  useEffect(() => {
    const slug = resolveSlug();
    const exitText = exitIntentText.trim();
    const pathname =
      typeof window !== 'undefined' ? window.location.pathname : '/';

    logPopup('hook', 'exit-intent effect', {
      enabled,
      chatOpen,
      pathname,
      slug,
      hasExitText: Boolean(exitText),
      moduleEnabled: modules?.isEnabled('exitIntent') ?? 'no-registry',
      dismissed: slug ? isExitIntentDismissed(slug) : false,
    });

    if (!enabled) {
      logPopup('exit-intent', 'skip — hook disabled');
      return;
    }
    if (!modules?.isEnabled('exitIntent') && modules != null) {
      logPopup('exit-intent', 'skip — exitIntent module off');
      return;
    }
    if (!slug) {
      logPopup('exit-intent', 'skip — no slug');
      return;
    }
    if (!apiBase) {
      logPopup('exit-intent', 'skip — no apiBase');
      return;
    }
    if (!exitText) {
      logPopup(
        'exit-intent',
        'skip — EXIT_INTENT_MESSAGE empty',
        { slug },
        'warn',
      );
      return;
    }

    if (slug && isExitIntentDismissed(slug)) {
      logPopup('exit-intent', 'skip — already dismissed this session', {
        slug,
        hint: 'clear sessionStorage teo_popup_exit:* or use Reset in playground',
      });
      return;
    }

    // Any page, once per session. Text = EXIT_INTENT_MESSAGE only (not /greeting).
    // first_dialog_reengage does not gate exit intent (timed context only).
    // Do not wait for human-gate here: the exit path itself is engagement, and
    // delaying attach races with the leave-to-top gesture (E2E + real users).
    return attachExitIntent(() => {
      if (chatOpenRef.current) {
        logPopup('exit-intent', 'trigger — chat open, skip show', {}, 'debug');
        return false;
      }
      if (slug && isExitIntentDismissed(slug)) return false;

      const shown = showPopup('exit-intent', exitText);
      if (shown && slug) {
        markHumanReadyFromUi('pointer');
        markExitIntentDismissed(slug);
        logPopup('exit-intent', 'marked dismissed for session', { slug });
      }
      return shown;
    });
  }, [
    apiBase,
    enabled,
    exitIntentText,
    locale,
    modules,
    resolveSlug,
    showPopup,
  ]);

  // After chat interaction: accumulate visible dwell (paused while chat open).
  // When threshold hits → unlock cycle → schedule popup; further pages keep
  // showing until the next chat interaction.
  useEffect(() => {
    if (!enabled) return;
    const slug = resolveSlug();
    if (!slug) return;
    if (settings.content.proactiveGreetingMode !== 'first_dialog_reengage') {
      return;
    }

    logPopup('reengage', 'start dwell tracking', {
      slug,
      reengageSeconds: settings.content.proactiveGreetingReengageSeconds,
    });

    return startProactiveDwellTracking({
      slug,
      reengageSeconds: settings.content.proactiveGreetingReengageSeconds,
      isPaused: () => chatOpenRef.current,
      onThreshold: () => {
        if (chatOpenRef.current) {
          logPopup('reengage', 'dwell ready — wait for chat close', { slug });
          return false;
        }
        logPopup('reengage', 'dwell → schedule context popup', { slug });
        return scheduleContextPopup();
      },
    });
  }, [
    enabled,
    resolveSlug,
    scheduleContextPopup,
    settings.content.proactiveGreetingMode,
    settings.content.proactiveGreetingReengageSeconds,
  ]);

  const scheduleContextPopupRef = useRef(scheduleContextPopup);
  scheduleContextPopupRef.current = scheduleContextPopup;
  const visitPathRef = useRef<string | null>(null);

  useEffect(() => {
    if (!enabled) {
      logPopup('hook', 'disabled — no navigation watcher');
      return;
    }

    const startPageVisit = (pathname: string, reason: string) => {
      pathnameRef.current = pathname;
      const slug = resolveSlug();
      const isNewVisit = visitPathRef.current !== pathname;
      visitPathRef.current = pathname;

      if (slug && isNewVisit) {
        clearContextPopupDismissed(slug, pathname);
        clearGreetingOfferState(slug, pathname);
        logPopup('context', 'new page visit — arm timer', {
          slug,
          pathname,
          reason,
        });
      } else {
        logPopup('context', 'same page visit — keep suppress', {
          pathname,
          reason,
          suppressed: slug ? isContextPopupDismissed(slug, pathname) : false,
        });
      }

      if (isNewVisit) {
        scheduleContextPopupRef.current();
      }
    };

    logPopup('hook', 'mount — schedule context + watch navigation');
    startPageVisit(window.location.pathname, 'mount');

    const stopNavigation = watchPageNavigation({
      onNavigate: (url) => {
        logPopup('navigation', 'url changed', { url });
        hidePopup();
        let nextPath: string;
        try {
          nextPath = new URL(url, window.location.origin).pathname;
        } catch {
          nextPath =
            typeof window !== 'undefined' ? window.location.pathname : '/';
        }
        startPageVisit(nextPath, 'navigate');
      },
    });

    return () => {
      logPopup('hook', 'unmount — cleanup');
      stopNavigation();
      cancelContextSchedule();
      // Allow remount on same pathname to count as a fresh schedule if React
      // remounts the hook without a real navigation.
      visitPathRef.current = null;
    };
  }, [cancelContextSchedule, enabled, hidePopup, resolveSlug]);

  return {
    popupVisible,
    popupMessage,
    popupCtaText,
    dismissPopup,
    acceptPopup,
    popupKind,
  };
}
